import { orderConfirmationBackTarget } from "../lib/orderConfirmationNavigation";
import DocumentAssignmentNotice from "../components/DocumentAssignmentNotice";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";

import { useSession } from "../auth/useSession";

import {
  fetchOrderConfirmationDetail,
  recordOrderConfirmationDecision,
  type OrderConfirmationLine,
  type OrderConfirmationLineStatus,
  type OrderConfirmationPositionSnapshot,
  type OrderConfirmationReferenceEvidence,
  type OrderConfirmationReviewAction,
  type OrderConfirmationReviewEvent,
} from "../api/orderConfirmations";
import { fetchProjects, type ProjectSummary } from "../api/projects";
import CostCenterValue from "../components/CostCenterValue";
import DocumentPdfActions from "../components/DocumentPdfActions";
import BelegDetailHeader from "../components/BelegDetailHeader";
import BelegReviewTimeline from "../components/BelegReviewTimeline";
import FigureRow from "../components/FigureRow";
import HistoryTable, { type HistoryRow } from "../components/HistoryTable";
import Spinner from "../components/Spinner";
import { cleanDescription } from "../lib/description";
import { historyCsv, historyCsvNumber } from "../lib/historyCsv";
import { downloadOfferCsv } from "../lib/offerCsv";
import { ApiError } from "../lib/api";
import {
  formatDateShort,
  formatEuro,
  formatQty,
  formatSignedEuro,
  formatSignedPercent,
  toNumber,
} from "../lib/format";
import {
  ORDER_CONFIRMATION_STATUS_LABELS,
  OrderConfirmationStatusBadge,
} from "./OrderConfirmationsPage";
import SectionInfo from "../components/SectionInfo";

const LINE_STATUS_LABELS: Record<OrderConfirmationLineStatus, string> = {
  ok: "Im Rahmen",
  over_reference: "Über Referenz",
  no_reference: "Ohne Referenz",
  unmatched: "Artikel nicht sicher zugeordnet",
  no_price: "Ohne Preis",
};

const LINE_STATUS_CLASSES: Record<OrderConfirmationLineStatus, string> = {
  ok: "bg-slate-100 text-slate-600 ring-slate-200",
  over_reference: "bg-rose-50 text-rose-800 ring-rose-200",
  no_reference: "bg-amber-50 text-amber-800 ring-amber-200",
  unmatched: "bg-amber-50 text-amber-800 ring-amber-200",
  no_price: "bg-slate-100 text-slate-700 ring-slate-200",
};

const ACTION_LABELS: Record<OrderConfirmationReviewAction, string> = {
  approve: "Freigegeben",
  query: "Rückfrage erforderlich",
  reject: "Nicht freigegeben",
  reopen: "Wieder geöffnet",
};

export default function OrderConfirmationReviewPage() {
  const location = useLocation();
  const backTarget = orderConfirmationBackTarget(location.state?.from);
  // The id comes from /belege/<id> (#546) or the old per-kind address.
  const { belegId, orderConfirmationId = belegId ?? "" } = useParams();
  // Wer bestellt, gibt die eigene Bestellung nicht selbst frei (#557).
  const canApprove = Boolean(
    useSession().user?.active_membership.can_approve_order_confirmations,
  );
  const queryClient = useQueryClient();
  const detailKey = [
    "order-confirmations",
    "detail",
    orderConfirmationId,
  ] as const;
  // Ohne Freigaberecht ist die Rückfrage die einzige Entscheidung (#557).
  const [action, setAction] = useState<Exclude<OrderConfirmationReviewAction, "reopen">>(
    canApprove ? "approve" : "query",
  );
  const [comment, setComment] = useState("");
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  const detailQuery = useQuery({
    queryKey: detailKey,
    queryFn: () => fetchOrderConfirmationDetail(orderConfirmationId),
    enabled: orderConfirmationId.length > 0,
  });

  const decisionMutation = useMutation({
    mutationFn: (nextAction: OrderConfirmationReviewAction) =>
      recordOrderConfirmationDecision({
        orderConfirmationId,
        action: nextAction,
        comment,
        warningAcknowledged:
          nextAction === "approve" ? warningAcknowledged : false,
        assessmentToken: detailQuery.data?.assessment_token ?? "",
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(detailKey, response.detail);
      void queryClient.invalidateQueries({
        queryKey: ["order-confirmations", "list"],
      });
      void queryClient.invalidateQueries({ queryKey: ["belege", "list"] });
      setComment("");
      setWarningAcknowledged(false);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        setWarningAcknowledged(false);
        void queryClient.invalidateQueries({ queryKey: detailKey });
      }
    },
  });

  if (detailQuery.isLoading) {
    return (
      <main className="mx-auto max-w-screen-2xl px-6 py-8">
        <Spinner label="Lade Auftragsbestätigung …" />
      </main>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <main className="mx-auto max-w-screen-2xl px-6 py-8">
        <p className="text-sm text-rose-700">
          Auftragsbestätigung konnte nicht geladen werden:{" "}
          {(detailQuery.error as Error | null)?.message ?? "Unbekannter Fehler"}
        </p>
        <Link
          to={backTarget.to}
          className="mt-3 inline-block text-sm text-emerald-700 hover:underline"
        >
          Zur Prüfliste
        </Link>
      </main>
    );
  }

  const detail = detailQuery.data;
  const document = detail.order_confirmation;
  const isOpen = document.review_status === "open";
  const requiresComment = action === "query" || action === "reject";
  const saveDisabled =
    decisionMutation.isPending ||
    (requiresComment && comment.trim().length === 0) ||
    (action === "approve" &&
      detail.summary.warning_count > 0 &&
      !warningAcknowledged);

  return (
    <main className="mx-auto max-w-screen-2xl px-6 py-8">
      <nav className="text-xs text-slate-500">
        <Link
          to={backTarget.to}
          className="hover:text-emerald-700 hover:underline"
        >
          {backTarget.label}
        </Link>{" "}
        /{" "}
        <span className="text-slate-700">
          {document.doc_number ?? orderConfirmationId}
        </span>
      </nav>

      <BelegDetailHeader
        docType="einzelauftrag"
        docId={document.order_confirmation_id}
        docNumber={document.doc_number}
        docDate={document.doc_date}
        supplier={document.supplier}
        pdfAvailable={document.pdf_available}
        positions={detail.lines.map((position) => ({
          projectKey: position.project_key,
          deliveryAddressDisplay: position.delivery_address_display,
        }))}
        status={<OrderConfirmationStatusBadge status={document.review_status} />}
      />
      <DocumentAssignmentNotice docId={document.order_confirmation_id} />

      <div className="mt-6">
        <FigureRow
          figures={[
            {
              label: "Bestätigungssumme",
              value: formatEuro(document.positions_total),
              hint: `${formatQty(document.line_count)} Positionen · ${formatQty(detail.summary.ok_count)} im Rahmen · ${formatQty(detail.summary.warning_count)} Warnungen`,
            },
            {
              label: "Mehrkosten",
              value: formatSignedEuro(detail.summary.positive_difference_total),
              hint: "Menge × Preis über Referenz",
              tone: Number(detail.summary.positive_difference_total) > 0 ? "negative" : undefined,
            },
            {
              label: "Ersparnis",
              value: formatSignedEuro(-(toNumber(detail.summary.saving_total) ?? 0)),
              hint: "Menge × Preis unter Referenz",
              tone: Number(detail.summary.saving_total) > 0 ? "positive" : undefined,
            },
            {
              label: "Summe Abweichungen",
              value: formatSignedEuro(detail.summary.net_delta_total),
              hint: "Mehrkosten und Ersparnis verrechnet",
              tone:
                Number(detail.summary.net_delta_total) > 0
                  ? "negative"
                  : Number(detail.summary.net_delta_total) < 0
                    ? "positive"
                    : undefined,
            },
            {
              label: "Möglich zum Bestpreis",
              value: formatEuro(detail.summary.best_price_saving_total),
              hint: "gegen niedrigsten Vergleichspreis",
            },
          ]}
        />
      </div>

      <PositionTable
        documentId={document.order_confirmation_id}
        documentNumber={document.doc_number}
        pdfAvailable={document.pdf_available}
        lines={detail.lines}
      />

      <DecisionPanel
        isOpen={isOpen}
        canApprove={canApprove}
        action={action}
        onAction={setAction}
        comment={comment}
        onComment={setComment}
        warningCount={detail.summary.warning_count}
        warningAcknowledged={warningAcknowledged}
        onWarningAcknowledged={setWarningAcknowledged}
        isPending={decisionMutation.isPending}
        error={
          decisionMutation.isError
            ? (decisionMutation.error as Error).message
            : null
        }
        saveDisabled={saveDisabled}
        onSave={() => decisionMutation.mutate(action)}
        onReopen={() => decisionMutation.mutate("reopen")}
        evidenceBoundary={detail.evidence_boundary.message}
      />

      <ReviewHistory events={detail.review_events} />
    </main>
  );
}

function DecisionPanel({
  canApprove,
  isOpen,
  action,
  onAction,
  comment,
  onComment,
  warningCount,
  warningAcknowledged,
  onWarningAcknowledged,
  isPending,
  error,
  saveDisabled,
  onSave,
  onReopen,
  evidenceBoundary,
}: {
  isOpen: boolean;
  action: Exclude<OrderConfirmationReviewAction, "reopen">;
  onAction: (
    action: Exclude<OrderConfirmationReviewAction, "reopen">,
  ) => void;
  comment: string;
  onComment: (comment: string) => void;
  warningCount: number;
  warningAcknowledged: boolean;
  onWarningAcknowledged: (checked: boolean) => void;
  isPending: boolean;
  error: string | null;
  saveDisabled: boolean;
  /** Freigeben und Ablehnen darf nur Controlling (#557). */
  canApprove: boolean;
  onSave: () => void;
  onReopen: () => void;
  evidenceBoundary: string;
}) {
  return (
    <section className="mt-8 border-t border-slate-200 pt-5">
      <h2 className="text-sm font-semibold text-slate-900">
        Entscheidung zum Beleg
      </h2>
      <p className="mt-1 text-xs leading-relaxed text-slate-600">{evidenceBoundary}</p>
      {isOpen ? (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {canApprove ? (
              <DecisionChoice
                active={action === "approve"}
                label="Freigeben"
                onClick={() => onAction("approve")}
              />
            ) : null}
            <DecisionChoice
              active={action === "query"}
              label="Rückfrage erforderlich"
              onClick={() => onAction("query")}
            />
            {canApprove ? (
              <DecisionChoice
                active={action === "reject"}
                label="Nicht freigeben"
                onClick={() => onAction("reject")}
              />
            ) : null}
          </div>
          {canApprove ? null : (
            <p className="mt-2 text-xs text-slate-600">
              Freigabe und Ablehnung liegen beim Controlling. Du kannst die
              Bestätigung prüfen und eine Rückfrage anregen.
            </p>
          )}
          <label className="mt-3 block">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              Kommentar
              {action === "query" || action === "reject"
                ? " (erforderlich)"
                : " (optional)"}
            </span>
            <textarea
              value={comment}
              onChange={(event) => onComment(event.target.value)}
              maxLength={2000}
              rows={3}
              placeholder={
                action === "query"
                  ? "Welche Position oder Preisänderung soll geklärt werden?"
                  : action === "reject"
                    ? "Warum wird diese Auftragsbestätigung nicht freigegeben?"
                    : "Interne Notiz zur Freigabe"
              }
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </label>
          {action === "approve" && warningCount > 0 ? (
            <label className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              <input
                type="checkbox"
                checked={warningAcknowledged}
                onChange={(event) =>
                  onWarningAcknowledged(event.target.checked)
                }
                className="mt-0.5 h-4 w-4 rounded border-amber-300 text-emerald-600"
              />
              <span>
                Ich habe alle {formatQty(warningCount)} Warnungen geprüft und
                gebe die Auftragsbestätigung trotz dieser Hinweise intern frei.
              </span>
            </label>
          ) : null}
          <button
            type="button"
            onClick={onSave}
            disabled={saveDisabled}
            className="mt-3 inline-flex h-9 items-center rounded-md bg-emerald-700 px-3 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? "Speichert …" : "Entscheidung speichern"}
          </button>
        </>
      ) : (
        <div className="mt-3">
          <p className="text-sm text-slate-600">
            Die Prüfung ist abgeschlossen. Für eine neue Entscheidung zuerst
            wieder öffnen; der bisherige Verlauf bleibt erhalten.
          </p>
          <button
            type="button"
            onClick={onReopen}
            disabled={isPending}
            className="mt-3 inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {isPending ? "Öffnet …" : "Wieder öffnen"}
          </button>
        </div>
      )}
      {error ? (
        <p className="mt-3 text-sm text-rose-700" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function DecisionChoice({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? "h-9 rounded-md border border-emerald-300 bg-emerald-50 px-3 text-sm font-medium text-emerald-800 ring-1 ring-emerald-100"
          : "h-9 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
      }
    >
      {label}
    </button>
  );
}

function PositionTable({
  documentId,
  documentNumber,
  pdfAvailable,
  lines,
}: {
  documentId: string;
  documentNumber: string | null;
  pdfAvailable: boolean;
  lines: OrderConfirmationLine[];
}) {
  const projectsQuery = useQuery({
    queryKey: ["projects", "beleg-detail", "all"],
    queryFn: () =>
      fetchProjects({ activeOnly: false, limit: 1000, withOverpay: false }),
    staleTime: 300_000,
    enabled: lines.some((line) => Boolean(line.project_key)),
  });
  const projectByKey = useMemo<ReadonlyMap<string, ProjectSummary>>(
    () =>
      new Map(
        (projectsQuery.data?.items ?? []).map((project) => [
          project.project_key,
          project,
        ]),
      ),
    [projectsQuery.data],
  );
  return (
    <section className="mt-6">
      <div className="mb-2">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-semibold text-slate-900">Positionen</h2>
          <SectionInfo label="Positionen">
            Referenz: gewichteter Rechnungspreis ausschließlich vor dem Belegdatum; 12 Monate, ersatzweise 24 Monate oder Gesamthistorie.
          </SectionInfo>
        </div>
      </div>
      <p className="mb-3 text-xs text-slate-600">Ein Bestellpreis ist noch nicht verfügbar. „–“ bedeutet unbekannt, nicht 0 €; die angezeigte Abweichung vergleicht den AB-Preis mit früheren Rechnungen.</p>
      <div data-testid="order-confirmation-review-table-scroll">
        <HistoryTable
          rows={lines.map((line): HistoryRow<OrderConfirmationLine> => ({
            id: line.line_pos,
            data: line,
            // All positions belong to this one document; keep their source order.
            date: null,
            article: <PositionArticle documentId={documentId} documentNumber={documentNumber} pdfAvailable={pdfAvailable} line={line} />,
            costCenter: <CostCenterValue projectKey={line.project_key} deliveryAddressDisplay={line.delivery_address_display} projectByKey={projectByKey} />,
            qty: line.qty,
            uom: line.uom,
            unitPrice: line.net_unit_price,
            reference: <PositionReference line={line} />,
            deviation: { amount: line.delta_abs, percent: line.delta_pct },
            deviationTotal: line.line_delta,
            status: <span className={"inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset " + LINE_STATUS_CLASSES[line.status]}>{LINE_STATUS_LABELS[line.status]}</span>,
            searchText: [line.article_code, line.manufacturer_article_code, line.description, line.project_key, line.delivery_address_display, line.project_key ? projectByKey.get(line.project_key)?.display_name : null].filter(Boolean).join(" ").toLowerCase(),
          }))}
          columns={["article", "costCenter", "qty", "orderedPrice", "unitPrice", "reference", "deviation", "deviationTotal", "status"]}
          onExport={exportRows => downloadOfferCsv(historyCsv([
            ["Position", "Artikel", "Bezeichnung", "Kostenstelle", "Menge", "Einheit", "Bestellt", "AB-Preis", "Historische Referenz", "Abweichung", "Abweichung %", "Abweichung gesamt", "Status"],
            ...exportRows.map(line => [line.line_pos, line.article_code ?? line.manufacturer_article_code, line.description, line.project_key ? projectByKey.get(line.project_key)?.display_name ?? line.project_key : line.delivery_address_display ?? "Nicht zugeordnet", historyCsvNumber(line.qty), line.uom, null, historyCsvNumber(line.net_unit_price), historyCsvNumber(line.reference_price), historyCsvNumber(line.delta_abs), historyCsvNumber(line.delta_pct), historyCsvNumber(line.line_delta), LINE_STATUS_LABELS[line.status]]),
          ]), "auftragsbestaetigung-positionen.csv")}
          labels={{ orderedPrice: "Bestellt", unitPrice: "AB-Preis" }}
          columnWidths={{ article: "21%", costCenter: "14%", qty: "6%", orderedPrice: "8%", unitPrice: "8%", reference: "12%", deviation: "9%", deviationTotal: "10%", status: "12%" }}
          minWidthClass="min-w-[1100px]"
          searchPlaceholder="Artikel oder Kostenstelle suchen …"
          emptyText="Diese Auftragsbestätigung enthält keine Positionen."
        />
      </div>
    </section>
  );
}

function PositionArticle({ documentId, documentNumber, pdfAvailable, line }: {
  documentId: string;
  documentNumber: string | null;
  pdfAvailable: boolean;
  line: OrderConfirmationLine;
}) {
  const articleCode = line.article_code ?? line.manufacturer_article_code ?? "Artikelnummer fehlt";
  return <div>
    <div className="flex items-center gap-1">
      {line.article_group_key && line.article_detail_available ? (
        <Link to={"/articles/" + encodeURIComponent(line.article_group_key) + "/preise"} className="min-w-0 text-xs font-medium text-emerald-700 hover:underline" title="Preisverlauf öffnen">{articleCode}</Link>
      ) : <span className="min-w-0 text-xs font-medium text-slate-900">{articleCode}</span>}
      {pdfAvailable ? <DocumentPdfActions
        docId={documentId} docNumber={documentNumber} linePos={line.line_pos}
        pageNo={typeof line.page_no === "number" ? line.page_no : Number(line.page_no) || null}
        sourceKind="pdf" compact previewOnly
      /> : null}
    </div>
    <p className="mt-1 text-xs text-slate-600">{cleanDescription(line.description) ?? "Keine Bezeichnung"}</p>
  </div>;
}

function PositionReference({ line }: { line: OrderConfirmationLine }) {
  const referenceLabel = line.reference_kind === "12m" ? "12 Monate" : line.reference_kind === "24m" ? "24 Monate" : line.reference_kind === "alltime" ? "Gesamthistorie" : null;
  return <div>
    <p className="font-medium tabular-nums text-slate-900">{formatEuro(line.reference_price)}</p>
    {referenceLabel ? <details className="mt-1">
      <summary className="cursor-pointer text-xs text-emerald-700 hover:underline">Grundlage ansehen</summary>
      <div className="mt-2 text-xs leading-relaxed text-slate-600">
        <p>{referenceLabel} · {formatQty(line.reference_count)} {line.reference_count === 1 ? "Rechnungsposition" : "Rechnungspositionen"}</p>
        <p>{formatDateShort(line.reference_first_date)} bis {formatDateShort(line.reference_last_date)}</p>
        {line.best_price !== null && line.best_price !== undefined ? <p>Bestpreis {formatEuro(line.best_price)}</p> : null}
      </div>
    </details> : <p className="mt-1 text-xs text-slate-600">Keine frühere Rechnung vorhanden</p>}
  </div>;
}

function ReviewHistory({
  events,
}: {
  events: OrderConfirmationReviewEvent[];
}) {
  return <BelegReviewTimeline
    entries={events.map((event) => ({
      id: event.event_id,
      createdAt: event.created_at,
      action: ACTION_LABELS[event.action],
      status: ORDER_CONFIRMATION_STATUS_LABELS[event.status],
      actorId: event.actor_user_id,
      actorLabel: event.actor_label,
      comment: event.comment,
      note: event.warning_acknowledged ? "Warnungen ausdrücklich bestätigt" : null,
      details: <ReviewBasis event={event} />,
    }))}
    emptyText="Noch keine gespeicherte Entscheidung."
  />;
}

function ReviewBasis({ event }: { event: OrderConfirmationReviewEvent }) {
  const documentNumber =
    typeof event.review_snapshot.doc_number === "string"
      ? event.review_snapshot.doc_number
      : event.order_doc_id;
  const pdfAvailable = event.review_snapshot.pdf_available === true;

  return (
    <details className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <summary className="cursor-pointer text-xs font-medium text-slate-700 hover:text-emerald-700">
        Gespeicherte Prüfgrundlage anzeigen
      </summary>
      <p className="mt-2 text-xs leading-relaxed text-slate-500">
        Diese Werte und Nachweise wurden bei der Entscheidung gespeichert. Eine
        spätere Preisaktualisierung verändert sie nicht.
      </p>
      <div className="mt-3 space-y-3">
        {event.position_snapshot.map((position, index) => (
          <SnapshotPosition
            key={(position.line_pos ?? "position") + "-" + String(index)}
            position={position}
            orderDocumentId={event.order_doc_id}
            orderDocumentNumber={documentNumber}
            orderPdfAvailable={pdfAvailable}
          />
        ))}
      </div>
    </details>
  );
}

function SnapshotPosition({
  position,
  orderDocumentId,
  orderDocumentNumber,
  orderPdfAvailable,
}: {
  position: OrderConfirmationPositionSnapshot;
  orderDocumentId: string;
  orderDocumentNumber: string;
  orderPdfAvailable: boolean;
}) {
  const referenceWindow =
    position.reference_kind === "12m"
      ? "12 Monate"
      : position.reference_kind === "24m"
        ? "24 Monate"
        : position.reference_kind === "alltime"
          ? "gesamte Historie"
          : null;
  const calculation =
    position.reference_calculation === "quantity_weighted_mean"
      ? "mengengewichteter Mittelwert"
      : position.reference_calculation === "arithmetic_mean"
        ? "arithmetischer Mittelwert"
        : null;
  const pageNo = Number(position.page_no);

  return (
    <article className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-slate-900">
            Position {position.line_pos ?? "–"} · {position.article_code ?? "Ohne Artikelnummer"}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {cleanDescription(position.description) || "Ohne Beschreibung"}
          </p>
        </div>
        <span
          className={
            "inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset " +
            LINE_STATUS_CLASSES[position.status]
          }
        >
          {LINE_STATUS_LABELS[position.status]}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-slate-500">AB-Preis</dt>
          <dd className="mt-0.5 font-medium text-slate-900">
            {formatEuro(position.net_unit_price)}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Referenz</dt>
          <dd className="mt-0.5 font-medium text-slate-900">
            {formatEuro(position.reference_price)}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Abweichung</dt>
          <dd className="mt-0.5 font-medium text-slate-900">
            {formatSignedEuro(position.delta_abs)} · {formatSignedPercent(position.delta_pct)}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Referenzbasis</dt>
          <dd className="mt-0.5 text-slate-700">
            {referenceWindow
              ? `${referenceWindow} · ${formatQty(position.reference_count)} Zeilen`
              : "Keine Referenz"}
          </dd>
        </div>
      </dl>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        {calculation ? <span>{calculation}</span> : null}
        {referenceWindow ? (
          <span>
            {formatDateShort(position.reference_first_date)} bis{" "}
            {formatDateShort(position.reference_last_date)}
          </span>
        ) : null}
        {orderPdfAvailable ? (
          <DocumentPdfActions
            docId={orderDocumentId}
            docNumber={orderDocumentNumber}
            linePos={position.line_pos}
            pageNo={Number.isFinite(pageNo) ? pageNo : null}
            sourceKind="pdf"
            label="AB-Position im PDF"
          />
        ) : null}
      </div>

      <SnapshotReferenceEvidence evidence={position.reference_evidence ?? []} />
    </article>
  );
}

function SnapshotReferenceEvidence({
  evidence,
}: {
  evidence: OrderConfirmationReferenceEvidence[];
}) {
  if (evidence.length === 0) {
    return (
      <p className="mt-3 text-xs text-slate-500">
        Für diese Position wurde keine historische Rechnungszeile verwendet.
      </p>
    );
  }

  return (
    <div className="mt-3 overflow-x-auto rounded-md border border-slate-200">
      <table className="min-w-[760px] text-xs">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="px-2 py-2 text-left font-medium">Datum</th>
            <th className="px-2 py-2 text-left font-medium">Rechnung</th>
            <th className="px-2 py-2 text-left font-medium">Position</th>
            <th className="px-2 py-2 text-right font-medium">Menge</th>
            <th className="px-2 py-2 text-right font-medium">Netto</th>
            <th className="px-2 py-2 text-left font-medium">Nachweis</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {evidence.map((source, index) => {
            const pageNo = Number(source.page_no);
            return (
              <tr
                key={(source.invoice_doc_id ?? "reference") + "-" + String(index)}
              >
                <td className="whitespace-nowrap px-2 py-2 text-slate-700">
                  {formatDateShort(source.doc_date)}
                </td>
                <td className="whitespace-nowrap px-2 py-2">
                  {source.invoice_doc_id ? (
                    <Link
                      to={`/belege/${encodeURIComponent(source.invoice_doc_id)}`}
                      className="font-mono text-emerald-700 hover:underline"
                    >
                      {source.doc_number ?? source.invoice_doc_id}
                    </Link>
                  ) : (
                    "–"
                  )}
                </td>
                <td className="px-2 py-2 text-slate-700">
                  {source.line_pos ?? "–"}
                </td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-700">
                  {formatQty(source.qty)} {source.uom ?? ""}
                </td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-900">
                  {formatEuro(source.net_unit_price)}
                </td>
                <td className="px-2 py-2">
                  {source.invoice_doc_id ? (
                    <DocumentPdfActions
                      docId={source.invoice_doc_id}
                      docNumber={source.doc_number}
                      linePos={source.line_pos}
                      pageNo={Number.isFinite(pageNo) ? pageNo : null}
                      sourceKind={source.source_kind ?? "pdf"}
                      label="Rechnungsposition im PDF"
                    />
                  ) : (
                    "–"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
